import { create } from 'zustand';

export type DialogId = 'export' | 'palette' | 'shortcuts' | 'settings' | null;

export const LIBRARY_SORT_KEYS = ['name', 'captured', 'imported', 'duration', 'kind', 'size'] as const;
export type LibrarySortKey = (typeof LIBRARY_SORT_KEYS)[number];
export interface LibrarySort {
  key: LibrarySortKey;
  descending: boolean;
}

interface UiState {
  dialog: DialogId;
  setDialog(dialog: DialogId): void;
  /** Incremented to ask the timeline to fit the sequence into view. */
  zoomToFitToken: number;
  requestZoomToFit(): void;
  libraryWidth: number;
  inspectorWidth: number;
  timelineHeight: number;
  setPanelSize(panel: 'libraryWidth' | 'inspectorWidth' | 'timelineHeight', px: number): void;
  /** How the media library is ordered (remembered across sessions). */
  librarySort: LibrarySort;
  setLibrarySort(sort: LibrarySort): void;
  /** The library folder being browsed; imports land here. `undefined` ⇒ top level. */
  libraryFolderId: string | undefined;
  setLibraryFolder(folderId: string | undefined): void;
}

const STORAGE_KEY = 'cutroom.layout';
const SORT_STORAGE_KEY = 'cutroom.librarySort';
const DEFAULT_SORT: LibrarySort = { key: 'imported', descending: false };

function loadSort(): LibrarySort {
  try {
    const raw = JSON.parse(localStorage.getItem(SORT_STORAGE_KEY) ?? 'null') as Partial<LibrarySort> | null;
    if (raw && LIBRARY_SORT_KEYS.includes(raw.key!) && typeof raw.descending === 'boolean')
      return { key: raw.key!, descending: raw.descending };
  } catch {
    /* storage unavailable or corrupt */
  }
  return DEFAULT_SORT;
}

function loadLayout(): Partial<UiState> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Partial<UiState>;
  } catch {
    return {};
  }
}

export const useUi = create<UiState>()((set, get) => ({
  dialog: null,
  setDialog: (dialog) => set({ dialog }),
  zoomToFitToken: 0,
  requestZoomToFit: () => set({ zoomToFitToken: get().zoomToFitToken + 1 }),
  libraryWidth: 300,
  inspectorWidth: 320,
  timelineHeight: 340,
  ...pick(loadLayout()),
  librarySort: loadSort(),
  setLibrarySort(librarySort) {
    set({ librarySort });
    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(librarySort));
    } catch {
      /* storage unavailable */
    }
  },
  libraryFolderId: undefined,
  setLibraryFolder: (libraryFolderId) => set({ libraryFolderId }),
  setPanelSize(panel, px) {
    set({ [panel]: Math.round(px) } as Pick<UiState, typeof panel>);
    const { libraryWidth, inspectorWidth, timelineHeight } = get();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ libraryWidth, inspectorWidth, timelineHeight }));
    } catch {
      /* storage unavailable */
    }
  },
}));

function pick(l: Partial<UiState>) {
  const out: Partial<Pick<UiState, 'libraryWidth' | 'inspectorWidth' | 'timelineHeight'>> = {};
  if (typeof l.libraryWidth === 'number') out.libraryWidth = l.libraryWidth;
  if (typeof l.inspectorWidth === 'number') out.inspectorWidth = l.inspectorWidth;
  if (typeof l.timelineHeight === 'number') out.timelineHeight = l.timelineHeight;
  return out;
}
